import React, { useEffect, useState } from 'react';
import useAuthRequest from '../../../services/requests.service';
import { Button, Modal, Table } from 'antd';
import { Bounce, toast } from 'react-toastify';
import moment from 'moment';
import AddSeatsModal from './AddSeatsModal';

const { confirm } = Modal;

const PlanCard = ({ plan, onSeatsUpdated }) => {
  const request = useAuthRequest();
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [activeCode, setActiveCode] = useState(plan.activeCode);
  const [showAddSeatsModal, setShowAddSeatsModal] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, [plan._id]);

  useEffect(() => {
    setActiveCode(plan.activeCode);
  }, [plan._id]);

  const fetchSubscriptions = async () => {
    setIsLoading(true);
    try {
      const res = await request.getDataAPI(
        `subscription?subscriptionPlan=${plan._id}&status=active&n=20&page=1`
      );
      setSubscriptions(res.subscriptions || []);
    } catch (e) {
      console.error('Failed to fetch subscriptions', e);
    } finally {
      setIsLoading(false);
    }
  };

  const isPermanent = plan.type === 'permanent';
  const cancelUnlockDate = new Date(
    new Date(plan.startdate).getTime() + 90 * 24 * 60 * 60 * 1000
  );
  const canCancel = isPermanent && new Date() > cancelUnlockDate;
  const cancelDateStr = cancelUnlockDate.toLocaleDateString('fr-FR');

  const handleRegenerateCode = () => {
    confirm({
      title: "Régénérer le code d'activation ?",
      content: "L'ancien code ne fonctionnera plus pour les nouveaux employés. Les employés déjà activés gardent leur accès.",
      okText: 'Régénérer',
      okType: 'danger',
      cancelText: 'Annuler',
      centered: true,
      async onOk() {
        setIsRegenerating(true);
        try {
          const res = await request.postDataAPI('subscriptionCode/regenerate', {
            subscriptionPlanId: plan._id,
          });
          setActiveCode(res.code);
          toast.success('Code régénéré avec succès', {
            position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
          });
        } catch (e) {
          toast.error('Erreur lors de la régénération du code', {
            position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
          });
        } finally {
          setIsRegenerating(false);
        }
      },
    });
  };

  const handleCancel = () => {
    confirm({
      title: "Annuler l'abonnement ?",
      content: "Vous serez redirigé vers le portail Stripe pour gérer la résiliation. Cette action est irréversible.",
      okText: 'Continuer',
      okType: 'danger',
      cancelText: 'Retour',
      centered: true,
      async onOk() {
        try {
          const res = await request.postDataAPI('stripe/portal-link', {
            customerId: plan.stripeCustomerId,
            url: window.location.href,
          });
          if (res.session?.url) window.location.href = res.session.url;
        } catch (e) {
          toast.error('Erreur lors de la redirection vers le portail', {
            position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
          });
        }
      },
    });
  };

  const handleRetireEmployee = (sub) => {
    confirm({
      title: "Retirer cet employé ?",
      content: "L'accès de l'employé sera immédiatement révoqué.",
      okText: 'Retirer',
      okType: 'danger',
      cancelText: 'Annuler',
      centered: true,
      async onOk() {
        try {
          const res = await request.deleteDataAPI(`deactivateSubscription/${sub._id}`);
          toast.success(res.msg || 'Employé retiré', {
            position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
          });
          fetchSubscriptions();
        } catch (e) {
          toast.error('Erreur lors du retrait', {
            position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
          });
        }
      },
    });
  };

  const columns = [
    {
      title: 'Nom/Prénom',
      render: (_, sub) => (
        <span>{sub.user?.firstname} {sub.user?.lastname}</span>
      ),
    },
    {
      title: 'Date activation',
      dataIndex: 'createdAt',
      render: (d) => moment(d).format('DD/MM/YYYY'),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      render: (status) => (
        <span style={{
          padding: '2px 8px',
          borderRadius: 4,
          backgroundColor: status === 'active' ? '#f6ffed' : '#fff1f0',
          color: status === 'active' ? '#52c41a' : '#ff4d4f',
          border: `1px solid ${status === 'active' ? '#b7eb8f' : '#ffa39e'}`,
          fontSize: 12,
        }}>
          {status === 'active' ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
    {
      title: 'Action',
      render: (_, sub) => (
        <Button size="small" danger onClick={() => handleRetireEmployee(sub)}>
          Retirer
        </Button>
      ),
    },
  ];

  const headerClass = isPermanent
    ? 'bg-royalblue-500'
    : 'bg-amber-500';

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div className={`${headerClass} px-4 py-3 flex justify-between items-center text-white`}>
        <div>
          <span className="font-bold text-sm">
            {isPermanent ? 'Plan Permanent' : 'Plan Saisonnier'}
          </span>
          <span className="text-xs ml-3 opacity-75">
            {isPermanent
              ? `depuis le ${moment(plan.startdate).format('DD/MM/YYYY')}`
              : `expire le ${moment(plan.enddate).format('DD/MM/YYYY')}`}
          </span>
        </div>
        <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
          {plan.max_account} sièges
        </span>
      </div>

      {/* Infos + actions */}
      <div className="px-4 py-3 flex flex-wrap justify-between items-center gap-3 bg-white border-b">
        <div>
          <span className="text-sm text-slate-500">Code d'activation : </span>
          <code className="text-sm font-mono bg-slate-50 border px-2 py-0.5 rounded">
            {activeCode}
          </code>
          <span className="text-xs text-slate-400 ml-3">
            {plan.activeCount}/{plan.max_account} actifs
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="small" loading={isRegenerating} onClick={handleRegenerateCode}>
            ↻ Régénérer code
          </Button>
          <Button size="small" type="primary" onClick={() => setShowAddSeatsModal(true)}>
            + Sièges
          </Button>
          {isPermanent && (
            <Button
              size="small"
              danger
              disabled={!canCancel}
              title={!canCancel ? `Résiliation possible à partir du ${cancelDateStr}` : ''}
              onClick={handleCancel}
            >
              Annuler {!canCancel && '🔒'}
            </Button>
          )}
        </div>
      </div>

      {/* Table employés */}
      <div className="p-4 bg-slate-50">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Employés actifs
        </div>
        <Table
          size="small"
          columns={columns}
          dataSource={subscriptions}
          rowKey="_id"
          loading={isLoading}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
        />
      </div>

      {showAddSeatsModal && (
        <AddSeatsModal
          plan={plan}
          onClose={() => setShowAddSeatsModal(false)}
          onSuccess={() => {
            setShowAddSeatsModal(false);
            onSeatsUpdated();
          }}
        />
      )}
    </div>
  );
};

export default PlanCard;
